import { compatibilityMatrix } from './compatibilityMatrix';

// Shared function to build connector families from compatibility matrix based on purpose
export function buildConnectorFamilies(purpose, options = {}) {
  const { requireHybrid = false } = options;
  const families = {};
  
  // Group connectors by family and purpose
  Object.values(compatibilityMatrix).forEach(connector => {
    // Handle comma-separated purposes like 'audio, video' or 'audio,video'
    const connectorPurposes = connector.purpose ? 
      connector.purpose.split(',').map(p => p.trim().toLowerCase()) : 
      [];
    
    let shouldInclude = false;
    
    if (requireHybrid) {
      // For hybrid builds, we need connectors that have BOTH the specific purpose AND 'hybrid'
      const hasSpecificPurpose = connectorPurposes.includes(purpose.toLowerCase());
      const hasHybridPurpose = connectorPurposes.includes('hybrid');
      shouldInclude = hasSpecificPurpose && hasHybridPurpose;
    } else {
      // Standard behavior - just check if connector has the purpose
      shouldInclude = connectorPurposes.includes(purpose.toLowerCase());
    }
    
    if (shouldInclude) {
      const familyKey = connector.type; // Use type for consistent grouping
      
      if (!families[familyKey]) {
        families[familyKey] = {
          id: familyKey.toLowerCase().replace(/\s+/g, '_'),
          name: connector.type || familyKey,
          description: getFamilyDescription(familyKey, purpose),
          image: connector.image,
          variants: []
        };
      }
      
      // Add this connector as a variant
      families[familyKey].variants.push({
        id: connector.id,
        name: connector.description,
        gender: connector.gender,
        image: connector.image,
        family: connector.family || connector.type,
        type: connector.type,
        mountType: connector.mountType,
        poles: connector.poles,
        purpose: connector.purpose
      });
    }
  });
  
  // Convert to array and sort
  const result = Object.values(families).sort((a, b) => a.name.localeCompare(b.name));
  
  // Sort variants within each family and set best family image
  result.forEach(family => {
    family.variants.sort((a, b) => {
      // For Speakon family, prioritize NL2 over NL4 panelmount
      if (family.id === 'speakon' || family.name === 'Speakon') {
        const aIsNL2 = a.id.includes('NL2');
        const bIsNL2 = b.id.includes('NL2');
        const aIsNL4Panel = a.id.includes('NL4') && a.mountType === 'panel';
        const bIsNL4Panel = b.id.includes('NL4') && b.mountType === 'panel';
        
        if (aIsNL2 && !bIsNL2) return -1;
        if (!aIsNL2 && bIsNL2) return 1;
        if (aIsNL4Panel && !bIsNL4Panel) return 1; // NL4 panelmount last
        if (!aIsNL4Panel && bIsNL4Panel) return -1;
      }
      
      // Sort by gender first (male before female), then by mountType
      if (a.gender !== b.gender) {
        return a.gender === 'male' ? -1 : 1;
      }
      if (a.mountType !== b.mountType) {
        return a.mountType === 'cable' ? -1 : 1; // cable mount first
      }
      return a.name.localeCompare(b.name);
    });
    
    // Set the best image for the family (prioritize non-placeholder images)
    const bestImage = family.variants.find(variant => 
      variant.image && 
      !variant.image.includes('placeholder') && 
      !variant.image.includes('coming_soon')
    )?.image || family.variants[0]?.image || family.image;
    
    family.image = bestImage;
  });
  
  return result;
}

// Helper function to get family descriptions
export function getFamilyDescription(family, purpose) {
  const descriptions = {
    'XLR': {
      'audio': 'Professional audio connectors for microphones and line signals',
      'power': 'Professional power connectors',
      'dmx': 'DMX lighting control connectors',
      'hybrid': 'Professional hybrid connectors for audio and power signals',
      'wye': 'Professional audio connectors for microphones and line signals'
    },
    'PowerCON': {
      'power': 'Neutrik PowerCON power connectors',
      'wye': 'Neutrik PowerCON power connectors'
    },
    'IEC': {
      'power': 'Standard equipment power connectors',
      'wye': 'Standard equipment power connectors'
    },
    'NEMA': {
      'power': 'Standard household/office power connectors',
      'wye': 'Standard household/office power connectors'
    },
    'TRUE1': {
      'power': 'Neutrik TRUE1 outdoor power connectors',
      'wye': 'Neutrik TRUE1 outdoor power connectors'
    },
    'TRS': {
      'audio': 'Balanced audio connectors',
      'hybrid': 'Balanced hybrid connectors for audio and power signals',
      'wye': 'Balanced audio connectors'
    },
    'TS': {
      'audio': 'Unbalanced audio connectors',
      'hybrid': 'Unbalanced hybrid connectors for audio and power signals',
      'wye': 'Unbalanced audio connectors'
    },
    'RCA': {
      'audio': 'Consumer audio/video connectors',
      'video': 'Consumer audio/video connectors',
      'hybrid': 'Consumer hybrid connectors for audio/video and power signals',
      'wye': 'Consumer audio/video connectors'
    },
    'Speakon': {
      'audio': 'Professional speaker connectors with locking mechanism',
      'hybrid': 'Professional hybrid connectors for speaker and power signals',
      'wye': 'Professional speaker connectors with locking mechanism'
    }
  };
  
  return descriptions[family]?.[purpose.toLowerCase()] || `${family} ${purpose} connectors`;
}
